/*
 * Strings for the CMMS settings page (features/cmmsSettings).
 *
 * The shared verbs — save, cancel, add, confirm — are not here. They live in
 * features/cmms/cmmsMessages.js and are the same words on every CMMS screen; a second copy is a
 * second thing for a translator to keep in step.
 */
import { defineMessages } from "react-intl";

export const scope = "axionoee.features.cmms.settings";

export default defineMessages({
  // page
  pageTitle: { id: `${scope}.page.title`, defaultMessage: "Maintenance settings" },
  pageSubtitle: {
    id: `${scope}.page.subtitle`,
    defaultMessage: "What the maintenance module picks from. Kept apart from the OEE settings, like the rest of the module.",
  },

  // tabs. The bar is here because settings arrive one at a time and each should be a tab rather
  // than another page in the menu.
  tabStorageLocations: { id: `${scope}.tab.storageLocations`, defaultMessage: "Storage locations" },
  tabSparePartTypes: { id: `${scope}.tab.sparePartTypes`, defaultMessage: "Spare part types" },
  tabTrades: { id: `${scope}.tab.trades`, defaultMessage: "Trades" },
  tabNotifications: { id: `${scope}.tab.notifications`, defaultMessage: "Email notifications" },

  // storage locations tab. No title of its own — the tab label is the title.
  storageLocationsSubtitle: {
    id: `${scope}.storageLocations.subtitle`,
    defaultMessage: "Where in the store a spare part sits. One place per part, and no stock per place — this is the list the register picks from.",
  },
  addStorageLocation: { id: `${scope}.storageLocations.add`, defaultMessage: "Add storage location" },

  /*
   * spare part types tab. No title of its own either; the tab label is the title.
   *
   * Everything below is namespaced under sparePartTypes. — and the storage location keys beside it
   * are not — because an id is an address, not a label. The unprefixed set was written when there
   * was one vocabulary on this page, so `column.name` reads "Storage location" and `toast.created`
   * means a shelf. Renaming them to match would orphan every translation that already points at
   * them. A second vocabulary names itself rather than renaming the first.
   *
   * What is genuinely generic is shared instead of copied: filterSearch, filterShowInactive,
   * filterClear, columnActive, statusActive, statusInactive, emptyFilteredHint, fieldName,
   * validationNameRequired, deactivateConfirm and restoreConfirm are all one word or one clause
   * with no shelf in them, and the notifications tab already borrows several.
   *
   * emptyFilteredTitle is not among them: it says "No storage locations match the filters", and
   * the notifications tab renders it too. That is an existing wart, not a licence for a third
   * caller — types get their own.
   */
  sparePartTypesSubtitle: {
    id: `${scope}.sparePartTypes.subtitle`,
    defaultMessage: "What sort of part it is — bearing, gasket, filter. One type per part, and no stock per type — this is the list the register picks from.",
  },
  addSparePartType: { id: `${scope}.sparePartTypes.add`, defaultMessage: "Add spare part type" },
  sparePartTypeColumnName: { id: `${scope}.sparePartTypes.column.name`, defaultMessage: "Type" },
  sparePartTypeActionEdit: { id: `${scope}.sparePartTypes.action.edit`, defaultMessage: "Rename spare part type" },
  sparePartTypeActionDeactivate: {
    id: `${scope}.sparePartTypes.action.deactivate`,
    defaultMessage: "Deactivate spare part type",
  },
  sparePartTypeActionRestore: {
    id: `${scope}.sparePartTypes.action.restore`,
    defaultMessage: "Restore spare part type",
  },
  sparePartTypesLoadFailed: {
    id: `${scope}.sparePartTypes.error.loadFailed`,
    defaultMessage: "Could not load the spare part types",
  },
  sparePartTypesEmptyTitle: {
    id: `${scope}.sparePartTypes.empty.title`,
    defaultMessage: "No spare part types yet",
  },
  sparePartTypesEmptyHint: {
    id: `${scope}.sparePartTypes.empty.hint`,
    defaultMessage: "Add the kinds of part the store holds — bearings, gaskets, filters. A spare part then picks one rather than having it typed in.",
  },
  sparePartTypesEmptyFilteredTitle: {
    id: `${scope}.sparePartTypes.empty.filteredTitle`,
    defaultMessage: "No spare part types match the filters",
  },
  sparePartTypeFormAddTitle: {
    id: `${scope}.sparePartTypes.form.addTitle`,
    defaultMessage: "Add spare part type",
  },
  sparePartTypeFormEditTitle: {
    id: `${scope}.sparePartTypes.form.editTitle`,
    defaultMessage: "Rename spare part type",
  },
  // Says what the list is for, because the answer decides how someone names the first one.
  sparePartTypeFieldNameHint: {
    id: `${scope}.sparePartTypes.field.nameHint`,
    defaultMessage: "What the kind is called on the floor. Renaming one renames it everywhere — the parts of that type follow.",
  },
  // Says what survives, because on this table almost everything does: the parts keep the type and
  // keep showing its name, and the server still accepts it when one of them is edited.
  sparePartTypeDeactivateTitle: {
    id: `${scope}.sparePartTypes.deactivate.title`,
    defaultMessage: "Deactivate spare part type",
  },
  sparePartTypeDeactivateMessage: {
    id: `${scope}.sparePartTypes.deactivate.message`,
    defaultMessage: "Deactivate \"{name}\"? Parts already of this type keep it and still show its name. It stops being offered when a part is given a type.",
  },
  sparePartTypeRestoreTitle: {
    id: `${scope}.sparePartTypes.restore.title`,
    defaultMessage: "Restore spare part type",
  },
  sparePartTypeRestoreMessage: {
    id: `${scope}.sparePartTypes.restore.message`,
    defaultMessage: "Restore \"{name}\"? It can be chosen again when a part is given a type.",
  },
  sparePartTypeToastCreated: {
    id: `${scope}.sparePartTypes.toast.created`,
    defaultMessage: "Spare part type created",
  },
  sparePartTypeToastUpdated: {
    id: `${scope}.sparePartTypes.toast.updated`,
    defaultMessage: "Spare part type updated",
  },
  sparePartTypeToastDeactivated: {
    id: `${scope}.sparePartTypes.toast.deactivated`,
    defaultMessage: "Spare part type deactivated",
  },
  sparePartTypeToastRestored: {
    id: `${scope}.sparePartTypes.toast.restored`,
    defaultMessage: "Spare part type restored",
  },
  sparePartTypeSaveFailed: {
    id: `${scope}.sparePartTypes.error.saveFailed`,
    defaultMessage: "Could not save the spare part type",
  },
  sparePartTypeDeactivateFailed: {
    id: `${scope}.sparePartTypes.error.deactivateFailed`,
    defaultMessage: "Could not deactivate the spare part type",
  },
  sparePartTypeRestoreFailed: {
    id: `${scope}.sparePartTypes.error.restoreFailed`,
    defaultMessage: "Could not restore the spare part type",
  },

  /*
   * trades (yrkesgrupper) tab. No title of its own; the tab label is the title.
   *
   * Namespaced under trades. for the reason given above the spare part types, and borrowing the
   * same generic strings they do.
   *
   * The wording says what a trade is and the one thing it controls: who is told about a work order
   * in the departments mapped to it (AB#1624). It grants no access and hides nothing, and a
   * sentence that implied otherwise would be read as a promise.
   */
  tradesSubtitle: {
    id: `${scope}.trades.subtitle`,
    defaultMessage: "What kind of work a technician does — electrician, mechanic, an outside contractor. A technician can be in several trades, and a trade can have nobody in it. Map a trade to the departments it serves, and notifications about work orders there go to its members instead of to the department's.",
  },
  addTrade: { id: `${scope}.trades.add`, defaultMessage: "Add trade" },
  tradeColumnName: { id: `${scope}.trades.column.name`, defaultMessage: "Trade" },
  tradeActionEdit: { id: `${scope}.trades.action.edit`, defaultMessage: "Rename trade" },
  tradeActionDeactivate: { id: `${scope}.trades.action.deactivate`, defaultMessage: "Deactivate trade" },
  tradeActionRestore: { id: `${scope}.trades.action.restore`, defaultMessage: "Restore trade" },
  tradesLoadFailed: { id: `${scope}.trades.error.loadFailed`, defaultMessage: "Could not load the trades" },
  tradesEmptyTitle: { id: `${scope}.trades.empty.title`, defaultMessage: "No trades yet" },
  tradesEmptyHint: {
    id: `${scope}.trades.empty.hint`,
    defaultMessage: "Add the trades your technicians work in — electricians, mechanics, an outside contractor. The technicians are then added to the trades they belong to.",
  },
  tradesEmptyFilteredTitle: {
    id: `${scope}.trades.empty.filteredTitle`,
    defaultMessage: "No trades match the filters",
  },
  tradeFormAddTitle: { id: `${scope}.trades.form.addTitle`, defaultMessage: "Add trade" },
  tradeFormEditTitle: { id: `${scope}.trades.form.editTitle`, defaultMessage: "Rename trade" },
  // An outside contractor is named here, because there is no other field for it: a supplier is a
  // trade with the supplier's name.
  tradeFieldNameHint: {
    id: `${scope}.trades.field.nameHint`,
    defaultMessage: "What the trade is called on the floor — Electrician, Mechanic, or the name of an outside contractor. Renaming one keeps its members.",
  },
  // Says what survives: the members and departments stay, so restoring it later is not a matter
  // of adding them back one by one. And what stops: a retired trade routes nothing, so its
  // departments fall back to whatever they would get without it.
  tradeDeactivateTitle: { id: `${scope}.trades.deactivate.title`, defaultMessage: "Deactivate trade" },
  tradeDeactivateMessage: {
    id: `${scope}.trades.deactivate.message`,
    defaultMessage: "Deactivate \"{name}\"? Its members and departments stay, and it can be restored at any time. Until then, work orders in its departments are routed as if it were not there.",
  },
  tradeRestoreTitle: { id: `${scope}.trades.restore.title`, defaultMessage: "Restore trade" },
  tradeRestoreMessage: {
    id: `${scope}.trades.restore.message`,
    defaultMessage: "Restore \"{name}\"? It comes back with the members and departments it had.",
  },
  tradeToastCreated: { id: `${scope}.trades.toast.created`, defaultMessage: "Trade created" },
  tradeToastUpdated: { id: `${scope}.trades.toast.updated`, defaultMessage: "Trade updated" },
  tradeToastDeactivated: { id: `${scope}.trades.toast.deactivated`, defaultMessage: "Trade deactivated" },
  tradeToastRestored: { id: `${scope}.trades.toast.restored`, defaultMessage: "Trade restored" },
  tradeSaveFailed: { id: `${scope}.trades.error.saveFailed`, defaultMessage: "Could not save the trade" },
  tradeDeactivateFailed: {
    id: `${scope}.trades.error.deactivateFailed`,
    defaultMessage: "Could not deactivate the trade",
  },
  tradeRestoreFailed: { id: `${scope}.trades.error.restoreFailed`, defaultMessage: "Could not restore the trade" },

  // trade members. Who is in the group, and the same care with the wording: membership says what
  // someone does, not what they may see.
  tradeColumnMembers: { id: `${scope}.trades.column.members`, defaultMessage: "Members" },
  tradeActionMembers: { id: `${scope}.trades.action.members`, defaultMessage: "Manage members" },
  // Not a warning. A trade with nobody in it is an outside contractor as often as it is a gap.
  tradeMembersNone: { id: `${scope}.trades.members.none`, defaultMessage: "No members" },
  // The server keeps a member who has lost the role and refuses to add one back, so the chip says
  // both halves — otherwise removing them looks undoable and is not.
  tradeMemberNotTechnicianHint: {
    id: `${scope}.trades.members.notTechnicianHint`,
    defaultMessage: "No longer has the Technician role. Stays a member until removed, but cannot be added again without the role.",
  },
  tradeMembersTitle: { id: `${scope}.trades.members.title`, defaultMessage: "Members of {name}" },
  tradeMembersFieldLabel: { id: `${scope}.trades.members.field`, defaultMessage: "Members" },
  tradeMembersFieldHint: {
    id: `${scope}.trades.members.fieldHint`,
    defaultMessage: "Technicians and admins. Someone can be in several trades, and a trade can be left empty — an outside contractor, say.",
  },
  tradeMembersStaleHint: {
    id: `${scope}.trades.members.staleHint`,
    defaultMessage: "No longer technicians: {names}. Saving keeps them in the trade. Once removed, they cannot be added again without the role.",
  },
  tradeToastMembersUpdated: { id: `${scope}.trades.toast.membersUpdated`, defaultMessage: "Members updated" },
  tradeMembersSaveFailed: {
    id: `${scope}.trades.error.membersSaveFailed`,
    defaultMessage: "Could not save the members",
  },
  // Members already in the trade are still shown and can still be removed; only adding is lost.
  tradeMembersUsersLoadFailed: {
    id: `${scope}.trades.error.usersLoadFailed`,
    defaultMessage: "Could not load the users, so nobody new can be added right now. Members already in the trade can still be removed.",
  },

  // trade departments (AB#1624). Where the trade works: a work order in one of these departments
  // is sent to the trade instead of to the department.
  tradeColumnDepartments: { id: `${scope}.trades.column.departments`, defaultMessage: "Departments" },
  tradeActionDepartments: { id: `${scope}.trades.action.departments`, defaultMessage: "Manage departments" },
  // Not a warning: a trade with no departments can still be given work orders by name.
  tradeDepartmentsNone: { id: `${scope}.trades.departments.none`, defaultMessage: "No departments" },
  // Routing still climbs through a deactivated department, so the mapping keeps working until it
  // is removed — and then it cannot be put back. The chip says both, as the stale member's does.
  tradeDepartmentInactiveHint: {
    id: `${scope}.trades.departments.inactiveHint`,
    defaultMessage: "Deactivated in the asset tree. Still sends its work orders to this trade until removed, but cannot be added again while deactivated.",
  },
  tradeDepartmentsTitle: { id: `${scope}.trades.departments.title`, defaultMessage: "Departments for {name}" },
  tradeDepartmentsFieldLabel: { id: `${scope}.trades.departments.field`, defaultMessage: "Departments" },
  // Nearest mapping wins, and this is the only place an admin is told so.
  tradeDepartmentsFieldHint: {
    id: `${scope}.trades.departments.fieldHint`,
    defaultMessage: "Work orders in these departments go to this trade instead of to the department. A sub-department follows its parent until it is given trades of its own, which then replace the parent's. Several trades can share a department.",
  },
  tradeDepartmentsStaleHint: {
    id: `${scope}.trades.departments.staleHint`,
    defaultMessage: "Deactivated: {names}. Saving keeps them on the trade. Once removed, they cannot be added again while deactivated.",
  },
  tradeDepartmentsInactiveTradeHint: {
    id: `${scope}.trades.departments.inactiveTradeHint`,
    defaultMessage: "This trade is deactivated, so its departments route nothing to it until it is restored.",
  },
  tradeToastDepartmentsUpdated: {
    id: `${scope}.trades.toast.departmentsUpdated`,
    defaultMessage: "Departments updated",
  },
  tradeDepartmentsSaveFailed: {
    id: `${scope}.trades.error.departmentsSaveFailed`,
    defaultMessage: "Could not save the departments",
  },
  tradeDepartmentsLoadFailed: {
    id: `${scope}.trades.error.departmentsLoadFailed`,
    defaultMessage: "Could not load the departments, so none can be added right now. Departments already on the trade can still be removed.",
  },

  /*
   * email notifications tab. No title of its own either; the tab label is the title.
   *
   * The subtitle names every route NotificationRecipientService actually uses, because most of them
   * are invisible on this page: the grid shows addresses and departments, and says nothing about a
   * work order dispatched to one person by name or about trades. The trade replaces the department
   * rather than joining it, so the department reads as the fallback it now is. Being emailed for
   * raising one is not among them and must not creep back into this wording — the service never
   * reads CreatedByUserId.
   */
  notificationsSubtitle: {
    id: `${scope}.notifications.subtitle`,
    defaultMessage: "Work order email reaches the person it is dispatched or assigned to, and the technicians of the trade doing the work — the work order's own trade, or the trades its department is mapped to on the Trades tab. Where no trade has anyone in it, the technicians and admins of the department it lands in, or any department above it, get it instead. An address is needed for it to arrive at. Turning someone off stops the mail on every route — they still see the work order in the app.",
  },
  columnUser: { id: `${scope}.notifications.columnUser`, defaultMessage: "User" },
  columnEmail: { id: `${scope}.notifications.columnEmail`, defaultMessage: "Email address" },
  columnRoles: { id: `${scope}.notifications.columnRoles`, defaultMessage: "Roles" },
  columnDepartments: { id: `${scope}.notifications.columnDepartments`, defaultMessage: "Departments" },
  columnEmailEnabled: { id: `${scope}.notifications.columnEmailEnabled`, defaultMessage: "Receives email" },
  // Shown instead of an address, because the switch cannot say this: no address means no mail
  // however it is set.
  noEmail: { id: `${scope}.notifications.noEmail`, defaultMessage: "No address" },
  noEmailHint: {
    id: `${scope}.notifications.noEmailHint`,
    defaultMessage: "This user has no email address, so they will not receive mail whatever this is set to. Add one on the users page.",
  },
  /*
   * The department equivalent, and deliberately not the same sentence. No address stops mail on
   * every route; no department stops one of them. Saying "will not receive mail" here would be
   * false for anyone in a trade, and for anyone a work order is dispatched to by name.
   */
  noDepartment: { id: `${scope}.notifications.noDepartment`, defaultMessage: "No department" },
  noDepartmentHint: {
    id: `${scope}.notifications.noDepartmentHint`,
    defaultMessage: "This user belongs to no department, so the department route never reaches them. They still receive mail through a trade they are in, and for a work order dispatched or assigned to them personally. Add a department on the users page.",
  },
  // A membership outlives its department being deactivated, and the mail resolver never looks at
  // the flag — so the chip is drawn muted rather than dropped, and says why.
  departmentInactiveHint: {
    id: `${scope}.notifications.departmentInactiveHint`,
    defaultMessage: "This department is deactivated in the asset tree. The membership still reaches this user — work order mail does not check the flag.",
  },
  role1: { id: `${scope}.notifications.role1`, defaultMessage: "Admin" },
  role2: { id: `${scope}.notifications.role2`, defaultMessage: "Superuser" },
  role3: { id: `${scope}.notifications.role3`, defaultMessage: "Operator" },
  role4: { id: `${scope}.notifications.role4`, defaultMessage: "Technician" },
  roleUnknown: { id: `${scope}.notifications.roleUnknown`, defaultMessage: "Role {id}" },
  notificationsLoadFailed: {
    id: `${scope}.notifications.loadFailed`,
    defaultMessage: "Could not load the notification settings",
  },
  toggleFailed: {
    id: `${scope}.notifications.toggleFailed`,
    defaultMessage: "Could not change the notification setting",
  },
  toastEnabled: { id: `${scope}.notifications.toastEnabled`, defaultMessage: "{name} will receive work order email" },
  toastDisabled: { id: `${scope}.notifications.toastDisabled`, defaultMessage: "{name} will no longer receive work order email" },
  notificationsEmptyTitle: {
    id: `${scope}.notifications.emptyTitle`,
    defaultMessage: "No technicians yet",
  },
  notificationsEmptyHint: {
    id: `${scope}.notifications.emptyHint`,
    defaultMessage: "Work order email goes to the technicians of the trade doing the work, or of the department it lands in when no trade has anyone. Give someone the Technician role on the users page, and they will appear here.",
  },

  // filters
  filterSearch: { id: `${scope}.filter.search`, defaultMessage: "Search by name" },
  filterShowInactive: { id: `${scope}.filter.showInactive`, defaultMessage: "Show inactive" },
  filterShowAllUsers: { id: `${scope}.filter.showAllUsers`, defaultMessage: "Show all users" },
  filterClear: { id: `${scope}.filter.clear`, defaultMessage: "Clear filters" },

  // grid columns
  columnName: { id: `${scope}.column.name`, defaultMessage: "Storage location" },
  columnActive: { id: `${scope}.column.active`, defaultMessage: "Status" },
  statusActive: { id: `${scope}.status.active`, defaultMessage: "Active" },
  statusInactive: { id: `${scope}.status.inactive`, defaultMessage: "Inactive" },

  // row actions
  actionEdit: { id: `${scope}.action.edit`, defaultMessage: "Rename storage location" },
  actionDeactivate: { id: `${scope}.action.deactivate`, defaultMessage: "Deactivate storage location" },
  actionRestore: { id: `${scope}.action.restore`, defaultMessage: "Restore storage location" },

  // empty + error states
  loadFailed: { id: `${scope}.error.loadFailed`, defaultMessage: "Could not load the storage locations" },
  emptyTitle: { id: `${scope}.empty.title`, defaultMessage: "No storage locations yet" },
  emptyHint: {
    id: `${scope}.empty.hint`,
    defaultMessage: "Add the shelves, cabinets and racks the store is divided into. A spare part then picks one rather than having it typed in.",
  },
  emptyFilteredTitle: { id: `${scope}.empty.filteredTitle`, defaultMessage: "No storage locations match the filters" },
  emptyFilteredHint: { id: `${scope}.empty.filteredHint`, defaultMessage: "Try clearing the filters." },

  // add / edit dialog
  formAddTitle: { id: `${scope}.form.addTitle`, defaultMessage: "Add storage location" },
  formEditTitle: { id: `${scope}.form.editTitle`, defaultMessage: "Rename storage location" },
  fieldName: { id: `${scope}.field.name`, defaultMessage: "Name" },
  // Says what the list is for, because the answer decides how someone names the first one.
  fieldNameHint: {
    id: `${scope}.field.nameHint`,
    defaultMessage: "What it is called on the floor. Renaming one renames it everywhere — the parts on it follow.",
  },

  // deactivate confirmation. Says what survives, because on this table almost everything does:
  // the parts stay where they are and keep showing the name.
  deactivateTitle: { id: `${scope}.deactivate.title`, defaultMessage: "Deactivate storage location" },
  deactivateMessage: {
    id: `${scope}.deactivate.message`,
    defaultMessage: "Deactivate \"{name}\"? Parts already there keep it and still show its name. It stops being offered when a part is given a location.",
  },
  deactivateConfirm: { id: `${scope}.deactivate.confirm`, defaultMessage: "Deactivate" },

  restoreTitle: { id: `${scope}.restore.title`, defaultMessage: "Restore storage location" },
  restoreMessage: {
    id: `${scope}.restore.message`,
    defaultMessage: "Restore \"{name}\"? It can be chosen again when a part is given a location.",
  },
  restoreConfirm: { id: `${scope}.restore.confirm`, defaultMessage: "Restore" },

  // validation. Uniqueness is not checked here — the server owns it and answers with the name
  // already holding it, which no message written in advance could say.
  validationNameRequired: { id: `${scope}.validation.nameRequired`, defaultMessage: "Name is required" },

  // toasts
  toastCreated: { id: `${scope}.toast.created`, defaultMessage: "Storage location created" },
  toastUpdated: { id: `${scope}.toast.updated`, defaultMessage: "Storage location updated" },
  toastDeactivated: { id: `${scope}.toast.deactivated`, defaultMessage: "Storage location deactivated" },
  toastRestored: { id: `${scope}.toast.restored`, defaultMessage: "Storage location restored" },
  saveFailed: { id: `${scope}.error.saveFailed`, defaultMessage: "Could not save the storage location" },
  deactivateFailed: { id: `${scope}.error.deactivateFailed`, defaultMessage: "Could not deactivate the storage location" },
  restoreFailed: { id: `${scope}.error.restoreFailed`, defaultMessage: "Could not restore the storage location" },
});
